import React from 'react'
import { WorkPeriodWithMission, WorkPeriodWithMissionAndSummary } from 'src/api/work-periods'
import { BetterTable, BetterTableColumnDefinition } from '../better-table'
import WorkerLabel from '../texts/worker-label'
import { getUserLabel } from 'src/utils/users'
import { Element } from 'react-bulma-components'
import { formatTime, localDate } from 'src/utils/date'
import Protected from '../protected/protected'
import { Link } from 'react-router-dom'
import useStore from 'src/store'
import { useValidateMutation } from 'src/queries/work-periods'
import {
  WorkPeriodAbandonedMessage,
  WorkPeriodCancelledMessage,
  WorkPeriodEventsMessage,
  WorkPeriodValidatedMessage,
} from './work-period-messages'
import WorkPeriodValidateButton from './validate-button'
import { getDocIdAsString } from '../../utils/documents'
import { getWorkPeriodCellData } from '../../utils/work-periods'
import { HourCell } from '../summary/table/table-cells'
import { WorkPeriodSummaryDateTooltipContent } from './tooltips/work-period-summary-date-tooltip-content'
import { sortByIgnoreCase } from '../../utils/arrays'
import WorkPeriodLink from './work-period-link'

const WorkPeriodList: React.FC<{
  workPeriods: WorkPeriodWithMission[]
  keys?: Array<keyof ReturnType<typeof useWorkPeriodsList>>
  omitKeys?: Array<keyof ReturnType<typeof useWorkPeriodsList>>
}> = ({
  workPeriods,
  keys = [
    'user',
    'day',
    'dates',
    'counted',
    'events',
    'interimAgency',
    'employer',
    'service',
    'jobTitle',
    'mission.dates',
    'isValidated',
    'isAbandoned',
    'validateAction',
  ],
  omitKeys = [],
}) => {
  const columns = useWorkPeriodsList()

  return (
    <BetterTable
      columns={columns}
      data={sortByIgnoreCase(workPeriods, 'mission.user.lastName')}
      keys={keys}
      omitKeys={omitKeys}
      noDataMessage="Aucune journée de travail à afficher"
    />
  )
}

export const useWorkPeriodsList =
  (): BetterTableColumnDefinition<WorkPeriodWithMissionAndSummary> => {
    const currentUserRole = useStore(s => s.session.currentUserRole)
    const workPeriodValidateMutation = useValidateMutation()

    const columns = {
      user: {
        Header: <WorkerLabel />,
        accessor: data => data.clockingRules._id,
        Cell: data => {
          const workPeriod = data.cell.row.original
          return (
            <Element
              renderAs={Link}
              to={`/workers/${getDocIdAsString(workPeriod.user)}`}
              textColor="primary"
            >
              <span style={{ whiteSpace: 'nowrap' }}>{getUserLabel(workPeriod.user)}</span>
            </Element>
          )
        },
      },
      day: {
        Header: 'Jour',
        Cell: data => {
          const workPeriod = data.cell.row.original
          return (
            <>
              <Protected roles={['employerMember']}>
                <WorkPeriodLink workPeriod={workPeriod} textColor="primary">
                  {localDate(new Date(workPeriod.start.date))}
                </WorkPeriodLink>
              </Protected>
              <Protected roles={['worker', 'interimAgencyMember']}>
                {localDate(new Date(workPeriod.start.date))}
              </Protected>
            </>
          )
        },
      },
      dates: {
        Header: 'Horaire',
        Cell: data => {
          const workPeriod = data.cell.row.original
          return (
            <>
              <Element renderAs="span" textColor="dark">
                {formatTime(new Date(workPeriod.start.date))}
              </Element>
              <br />
              <Element renderAs="span" textColor="dark">
                {formatTime(new Date(workPeriod.end.date))}
              </Element>
            </>
          )
        },
        width: 1,
      },
      counted: {
        Header: 'Presté',
        Cell: data => {
          const workPeriod = data.cell.row.original
          const startCellData = getWorkPeriodCellData(workPeriod, 'start')
          const endCellData = getWorkPeriodCellData(workPeriod, 'end')
          return (
            <>
              <HourCell
                date={startCellData.date}
                textColor={startCellData.color}
                tooltipId={startCellData.tooltipId}
                tooltipContent={
                  <WorkPeriodSummaryDateTooltipContent workPeriod={workPeriod} type="start" />
                }
              />
              <HourCell
                date={endCellData.date}
                textColor={endCellData.color}
                tooltipId={endCellData.tooltipId}
                tooltipContent={
                  <WorkPeriodSummaryDateTooltipContent workPeriod={workPeriod} type="end" />
                }
              />
            </>
          )
        },
        width: 1,
      },
      interimAgency:
        currentUserRole !== 'interimAgencyMember'
          ? {
              Header: 'Agence',
              id: 'interimAgency',
              Cell: data => {
                const workPeriod = data.cell.row.original
                return workPeriod.mission.interimAgency?.name ?? null
              },
            }
          : null,
      employer:
        currentUserRole !== 'employerMember'
          ? {
              Header: 'EU',
              id: 'employer',
              Cell: data => {
                const workPeriod = data.cell.row.original
                return workPeriod.mission.employer?.name || ' '
              },
            }
          : null,
      service: {
        Header: 'Affectation',
        id: 'service',
        accessor: 'service.name',
      },
      jobTitle: {
        Header: 'Métier',
        id: 'jobTitle',
        accessor: 'mission.jobTitle.name',
      },
      isValidated: {
        Header: (
          <WorkPeriodValidatedMessage
            workPeriod={{ isValidated: true }}
            type="icon"
            small
            iconColor={'dark'}
          />
        ),
        Cell: data => {
          const workPeriod = data.cell.row.original
          return (
            <>
              <WorkPeriodValidatedMessage workPeriod={workPeriod} type="icon" small />
            </>
          )
        },
        width: 1,
      },
      isAbandoned: {
        Header: (
          <WorkPeriodAbandonedMessage
            workPeriod={{ isAbandoned: true }}
            type="icon"
            small
            iconColor={'dark'}
          />
        ),
        Cell: data => {
          const workPeriod = data.cell.row.original
          return <WorkPeriodAbandonedMessage workPeriod={workPeriod} type="icon" small />
        },
        width: 1,
      },
      isCancelled: {
        Header: (
          <WorkPeriodCancelledMessage
            workPeriod={{ isCancelled: true }}
            type="icon"
            small
            iconColor={'dark'}
          />
        ),
        Cell: data => {
          const workPeriod = data.cell.row.original
          return <WorkPeriodCancelledMessage workPeriod={workPeriod} type="icon" small />
        },
        width: 1,
      },
      'mission.dates': {
        Header: 'Mission',
        Cell: data => {
          const workPeriod = data.cell.row.original
          return (
            <>
              {localDate(workPeriod.mission.start)}&nbsp;-&nbsp;
              {workPeriod.mission.end ? localDate(workPeriod.mission.end) : '/'}
            </>
          )
        },
      },
      events: {
        Header: (
          <WorkPeriodEventsMessage
            workPeriod={{
              events: [
                {
                  //@ts-expect-error force display
                  item: {},
                },
              ],
            }}
            type="icon"
            small
            iconColor={'dark'}
            text="Evènements"
          />
        ),
        Cell: data => {
          const workPeriod = data.cell.row.original
          return (
            <>
              <WorkPeriodEventsMessage workPeriod={workPeriod} type="icon" small />
            </>
          )
        },
      },
      validateAction: {
        Header: 'Valider',
        Cell: data => {
          const workPeriod = data.cell.row.original
          return (
            <Protected
              roles={['employerMember']}
              action={{ resource: 'missions', name: 'validateWorkPeriod' }}
            >
              <WorkPeriodValidateButton
                workPeriod={workPeriod}
                size="smallest"
                onClick={() => {
                  workPeriodValidateMutation.mutate({
                    workPeriodId: workPeriod._id,
                    isValidated: !workPeriod.isValidated,
                  })
                }}
              />
            </Protected>
          )
        },
      },
    } satisfies BetterTableColumnDefinition<WorkPeriodWithMissionAndSummary>

    return columns
  }

export default WorkPeriodList
